import { CheckCheck, MoreHorizontal, Truck, XCircle } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import type { RedemptionStatus } from "@/components/creator/rewards/redemption-history-row"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useMenuDialogHandoff } from "@/hooks/use-menu-dialog-handoff"

import { CancelRedemptionDialog } from "./cancel-redemption-dialog"
import { FulfillRedemptionDialog } from "./fulfill-redemption-dialog"
import { MarkShippedDialog } from "./mark-shipped-dialog"

/**
 * OKS-61 — Per-row kebab + dialog host for ACTIONABLE Redemption rows in
 * the brand-side fulfillment queue (UX Spec §2 actions cell).
 *
 * Renders on non-terminal rows only (the queue's cell renders an em-dash
 * placeholder for terminal rows directly — UX-OQ-8). OKS-563 widened
 * "actionable" from pending-only to pending + shipped: a shipped
 * redemption keeps its kebab, because PRD R4 requires it to stay
 * actionable rather than reading as finished. Mirrors the
 * `reward-row-actions.tsx` pattern from OKS-59:
 *
 *   - shadcn `DropdownMenu` with `MoreHorizontal` trigger
 *   - `DropdownMenuItem`s: Mark shipped (pending only), Fulfill /
 *     Mark delivered, Cancel (destructive)
 *   - Each opens its matching dialog (locally managed open state — the
 *     dialogs are siblings, never simultaneously open). Note the
 *     mark-shipped one is a `Dialog`, not an `AlertDialog`: it takes
 *     input and is not destructive (UX-OQ-1).
 *
 * Touch target: shadcn `Button size="icon"` is 36px; we bump to
 * h-11 w-11 to stay ≥ 44px on mobile (UX Spec §7 accessibility floor;
 * mirrors OKS-59's `reward-row-actions.tsx`).
 *
 * Accessibility:
 *   - DropdownMenu trigger carries `aria-label` describing the target
 *     redemption (UX Spec §8).
 *   - Dialog focus trap + return-to-trigger via shadcn defaults.
 */
interface RedemptionRowActionsProps {
  redemptionId: number
  creatorName: string
  rewardTitle: string
  coinCost: number
  rewardIsUnlimited: boolean
  /**
   * OKS-563 — drives which items the kebab offers. Pending gets
   * "Mark as shipped"; shipped drops it (marking is one-way, PRD R2)
   * and relabels the fulfil item to "Mark as delivered".
   */
  status: RedemptionStatus
}

export function RedemptionRowActions({
  redemptionId,
  creatorName,
  rewardTitle,
  coinCost,
  rewardIsUnlimited,
  status,
}: RedemptionRowActionsProps) {
  const { t } = useTranslation()
  const [markShippedOpen, setMarkShippedOpen] = useState(false)
  const [fulfillOpen, setFulfillOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)
  const { finalFocus, yieldFocusToDialog } = useMenuDialogHandoff()

  // PRD R2 — marking shipped is one-way; there is no un-ship. So the item
  // DISAPPEARS on a shipped row rather than rendering disabled: a disabled
  // control invites the admin to hunt for the precondition that would
  // enable it, and there isn't one.
  const canMarkShipped = status === "pending"

  // Same action, same endpoint, different verb. On a shipped row
  // "Fulfill" is the wrong word — the parcel is already out, and what the
  // admin is confirming is arrival (UX Spec §2.1).
  const fulfillLabelKey =
    status === "shipped"
      ? "rewards.redemptions.row_actions.mark_delivered"
      : "rewards.redemptions.row_actions.fulfill"

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-11 w-11"
              aria-label={t("rewards.redemptions.row_actions.menu_label", {
                creator: creatorName,
                reward: rewardTitle,
              })}
              data-testid="redemption-row-actions-trigger"
              data-redemption-id={redemptionId}
            >
              <MoreHorizontal className="size-4" aria-hidden="true" />
            </Button>
          }
        />
        <DropdownMenuContent
          align="end"
          finalFocus={finalFocus}
          data-testid="redemption-row-actions-menu"
        >
          {canMarkShipped && (
            <DropdownMenuItem
              onClick={() => {
                yieldFocusToDialog()
                setMarkShippedOpen(true)
              }}
              className="flex items-center gap-2"
              data-testid="redemption-row-action-mark-shipped"
            >
              <Truck className="size-4" aria-hidden="true" />
              {t("rewards.redemptions.row_actions.mark_shipped")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => {
              yieldFocusToDialog()
              setFulfillOpen(true)
            }}
            className="flex items-center gap-2"
            data-testid="redemption-row-action-fulfill"
          >
            <CheckCheck className="size-4" aria-hidden="true" />
            {t(fulfillLabelKey)}
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              yieldFocusToDialog()
              setCancelOpen(true)
            }}
            className="flex items-center gap-2"
            data-testid="redemption-row-action-cancel"
          >
            <XCircle className="size-4" aria-hidden="true" />
            {t("rewards.redemptions.row_actions.cancel")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <MarkShippedDialog
        open={markShippedOpen}
        onOpenChange={setMarkShippedOpen}
        redemptionId={redemptionId}
        creatorName={creatorName}
        rewardTitle={rewardTitle}
      />
      <FulfillRedemptionDialog
        open={fulfillOpen}
        onOpenChange={setFulfillOpen}
        redemptionId={redemptionId}
        creatorName={creatorName}
        rewardTitle={rewardTitle}
        coinCost={coinCost}
      />
      <CancelRedemptionDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        redemptionId={redemptionId}
        creatorName={creatorName}
        rewardTitle={rewardTitle}
        coinCost={coinCost}
        rewardIsUnlimited={rewardIsUnlimited}
      />
    </>
  )
}
